import { PATH_MAIN } from '../../../routes/paths';
import { ClientPermission, UserRole } from '../../../enums/shared.enums';
import { ADMIN_ROLES, PAYER_ROLES } from '../../../constants/common.constants';
import Iconify from 'src/components/Iconify';
import { JWTContextType } from '../../../@types/auth.types';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import { Permission } from '../../../@types/shared.types';
import { User } from '../../../@types/common.types';
import { ROLE_PERMISSION_MAP } from '../../../constants/shared.constants';
import { t as translate } from 'i18next';
import { ICON } from '../../../config';

const IconifyIcon = ({ name }: { name: string }) => (
    <Iconify icon={name} width={ICON.NAVBAR_ITEM_HORIZONTAL} height={ICON.NAVBAR_ITEM_HORIZONTAL} />
);

const ICONS = {
    dashboard: <IconifyIcon name="material-symbols:dashboard-outline" />,
    calendar: <IconifyIcon name="material-symbols:calendar-today-outline" />,
    booking: <IconifyIcon name="ic_booking" />,
    availability: <IconifyIcon name="material-symbols:schedule-outline" />,
    menuItem: <IconifyIcon name="ic_menu_item" />,
    plans: <IconifyIcon name="material-symbols:sticky-note-2-outline" />,
    assessments: <IconifyIcon name="mdi:clipboard-check-outline" />,
    assignments: <IconifyIcon name="material-symbols:assignment-outline" />,
    myAccount: <IconifyIcon name="material-symbols:person-outline" />,
    knowledgeBase: <IconifyIcon name="mdi:bookshelf" />,
    client: <IconifyIcon name="mdi:people-outline" />,
    groups: <IconifyIcon name="material-symbols:groups-outline" />,
    messages: <IconifyIcon name="mdi:message-outline" />,
    escalations: <IconifyIcon name="material-symbols:arrow-warm-up" />,
    providerFeedbackLog: <IconifyIcon name="material-symbols:list-alt-outline" />,
    providers: {
        client: (
            <VolunteerActivismOutlinedIcon
                sx={{ width: ICON.NAVBAR_ITEM_HORIZONTAL, height: ICON.NAVBAR_ITEM_HORIZONTAL }}
            />
        ),
        admin: <IconifyIcon name="material-symbols:psychology-outline" />,
    },
    claims: <IconifyIcon name="material-symbols:request-quote-outline" />,
    settings: <IconifyIcon name="material-symbols:settings-outline" />,
    payouts: <IconifyIcon name="mdi:bank-outline" />,
    journal: <IconifyIcon name="material-symbols:book-2-outline" />,
    organizations: <IconifyIcon name="mynaui:store" />,
    chatbot: <IconifyIcon name="ic_chatbot2" />,
    community: <IconifyIcon name="material-symbols:diversity-2-outline" />,
    fax: <IconifyIcon name="material-symbols:fax-outline" />,
    members: <IconifyIcon name="ic:outline-groups" />,
};

export interface NavConfigOptions {
    messaging?: {
        unreadConversationCount?: number;
        isEnabled?: boolean;
    };
}

// Construct the navigation config based on the user's role
const navConfig = (context: JWTContextType, options?: NavConfigOptions) => {
    const { userRole: role, user, hasPermission } = context;

    // Null check
    if (!user) return [];

    // Payer experience (PayerAdmin, NetworkPartnerAdmin)
    if (PAYER_ROLES.includes(role)) {
        return payerAdminConfig(context);
    }

    // Orgnization Admin
    if (role === UserRole.ORGANIZATION_ADMIN) {
        return orgAdminConfig(context);
    }

    // Normal User
    return normalUserConfig(user, hasPermission, options);
};

const payerAdminConfig = (context: JWTContextType) => {
    const { payer, userRole } = context;

    let config = [
        {
            subheader: payer?.name ?? `${translate('layouts.navConfig.general')}`,
            items: [
                {
                    title: `${translate('layouts.navConfig.dashboard')}`,
                    path: PATH_MAIN.general.dashboard,
                    icon: ICONS.dashboard,
                },
                {
                    title: `${translate('layouts.navConfig.members')}`,
                    path: PATH_MAIN.general.members,
                    icon: ICONS.members,
                },
                {
                    title: `${translate('layouts.navConfig.providers')}`,
                    path: PATH_MAIN.general.providers,
                    icon: ICONS.providers.admin,
                },
            ],
        },
        {
            subheader: `${translate('layouts.navConfig.account')}`,
            items: [
                {
                    title: `${translate('layouts.navConfig.myAccount')}`,
                    path: PATH_MAIN.account,
                    icon: ICONS.myAccount,
                },
                ...(userRole === UserRole.NETWORK_PARTNER_ADMIN
                    ? [
                          {
                              title: `${translate('layouts.navConfig.organizationSettings')}`,
                              path: PATH_MAIN.payer.settings,
                              icon: ICONS.settings,
                          },
                      ]
                    : []),
            ],
        },
    ];
    return config;
};

const orgAdminConfig = (context: JWTContextType) => {
    const { orgPreview } = context;

    let config = [
        {
            subheader:
                (orgPreview?.nickName || orgPreview?.abbreviation) ??
                `${translate('layouts.navConfig.organizations')}`,
            items: [
                {
                    title: `${translate('layouts.navConfig.dashboard')}`,
                    path: PATH_MAIN.general.dashboard,
                    icon: ICONS.dashboard,
                },
                {
                    title: `${translate('layouts.navConfig.members')}`,
                    path: PATH_MAIN.organization.members,
                    icon: ICONS.client,
                    roles: [UserRole.ORGANIZATION_ADMIN],
                },
                {
                    title: `${translate('layouts.navConfig.settings')}`,
                    path: PATH_MAIN.organization.settings,
                    icon: ICONS.settings,
                    roles: [UserRole.ORGANIZATION_ADMIN],
                },
            ],
        },
        {
            subheader: `${translate('layouts.navConfig.account')}`,
            items: [
                {
                    title: `${translate('layouts.navConfig.myAccount')}`,
                    path: PATH_MAIN.account,
                    icon: ICONS.myAccount,
                },
            ],
        },
    ];
    return config;
};

const normalUserConfig = (
    user: User,
    hasPermission: (permission: Permission) => boolean,
    options?: NavConfigOptions
) => {
    const { country } = user;

    const isClient = user.role.name === UserRole.CLIENT;

    const { isTherapySupported } = country;

    const items = [
        {
            title: `${translate('layouts.navConfig.dashboard')}`,
            path: PATH_MAIN.general.dashboard,
            icon: ICONS.dashboard,
        },
        {
            title: `${translate('layouts.navConfig.appointments')}`,
            path: PATH_MAIN.general.appointments,
            icon: ICONS.calendar,
            isTherapySupportRequired: true,
        },
        {
            title: `${translate('layouts.navConfig.providers')}`,
            path: PATH_MAIN.general.providers,
            icon: isClient ? ICONS.providers.client : ICONS.providers.admin,
            roles: [...ADMIN_ROLES, UserRole.CLIENT],
            isTherapySupportRequired: isClient ? isTherapySupported : false,
        },
        {
            title: `${translate('layouts.navConfig.clients')}`,
            path: PATH_MAIN.general.clients,
            icon: ICONS.client,
            roles: [UserRole.PROVIDER, ...ADMIN_ROLES],
        },
        {
            title: `${translate('layouts.navConfig.assessments')}`,
            path: PATH_MAIN.general.assessments,
            icon: ICONS.assessments,
            roles: [UserRole.CLIENT],
        },
        {
            title: 'assignments',
            path: PATH_MAIN.general.assignments,
            icon: ICONS.assignments,
            roles: [UserRole.CLIENT],
            isTherapySupportRequired: true,
        },
        {
            title: `${translate('layouts.navConfig.assignmentLibrary')}`,
            path: PATH_MAIN.general.assignmentLibrary,
            icon: ICONS.assignments,
            roles: [UserRole.PROVIDER, ...ADMIN_ROLES],
            isTherapySupportRequired: true,
        },

        {
            title: `${translate('layouts.navConfig.plans')}`,
            path: PATH_MAIN.general.plans,
            icon: ICONS.plans,
            roles: [UserRole.CLIENT],
            isTherapySupportRequired: true,
        },
        {
            title: `${translate('layouts.navConfig.groups')}`,
            path: PATH_MAIN.general.groups,
            icon: ICONS.groups,
            isTherapySupportRequired: true,
            isGroupsSupportRequired: true,
            roles: [UserRole.PROVIDER, UserRole.CLIENT, UserRole.SUPER_ADMIN, UserRole.CC_MANAGER],
        },
        {
            title: `${translate('layouts.navConfig.providerFeedbackLog')}`,
            path: PATH_MAIN.general.providerFeedbackLog,
            icon: ICONS.providerFeedbackLog,
            roles: ADMIN_ROLES,
        },
        {
            title: `${translate('layouts.navConfig.availability')}`,
            path: PATH_MAIN.general.availability,
            icon: ICONS.availability,
            roles: [UserRole.PROVIDER],
        },
        {
            title: `${translate('layouts.navConfig.messages')}`,
            path: PATH_MAIN.general.messages,
            icon: ICONS.messages,
            isTherapySupportRequired: true,
            isMessagingSupportRequired: true,
            roles: [UserRole.PROVIDER, UserRole.CLIENT],
            badgeContent: options?.messaging?.unreadConversationCount,
        },
    ];

    let config = [
        {
            subheader: `${translate('layouts.navConfig.general')}`,
            items: items.filter((item: any) => {
                if (item.isTherapySupportRequired && !isTherapySupported) return false;
                if (item.isGroupsSupportRequired && isClient && user.groupsEnabled === false)
                    return false;
                if (item.isMessagingSupportRequired && !(options?.messaging?.isEnabled ?? true))
                    return false;
                return true;
            }),
        },
    ];

    // Tools
    const toolItems = [];

    if (isClient) {
        toolItems.push({
            title: `${translate('layouts.navConfig.journal')}`,
            path: PATH_MAIN.general.journals,
            icon: ICONS.journal,
            roles: [UserRole.CLIENT],
        });
    }

    if (user.role.name === UserRole.PROVIDER) {
        toolItems.push({
            title: `${translate('layouts.navConfig.knowledgeBase')}`,
            path: PATH_MAIN.provider.knowledgeBase,
            icon: ICONS.knowledgeBase,
            roles: [UserRole.PROVIDER],
        });
    }

    // Escalations for super admins and cc managers
    if (user.role.name === UserRole.SUPER_ADMIN || user.role.name === UserRole.CC_MANAGER) {
        toolItems.push({
            title: `${translate('layouts.navConfig.escalations')}`,
            path: PATH_MAIN.general.escalations,
            icon: ICONS.escalations,
            roles: [UserRole.SUPER_ADMIN, UserRole.CC_MANAGER],
        });
    }

    // Conditionally construct the tools section
    if (toolItems.length > 0) {
        config.push({
            subheader: `${translate('layouts.navConfig.tools')}`,
            items: toolItems,
        } as any);
    }

    // Admin
    let adminItems = [];
    if (hasPermission(ClientPermission.READ_CLAIMS)) {
        adminItems.push({
            title: `${translate('layouts.navConfig.claims')}`,
            path: PATH_MAIN.general.claims,
            icon: ICONS.claims,
            roles: [...ROLE_PERMISSION_MAP[ClientPermission.READ_CLAIMS], UserRole.SUPER_ADMIN],
        });
    }
    if (user.role.name === UserRole.SUPER_ADMIN) {
        adminItems.push(
            {
                title: `${translate('layouts.navConfig.payouts')}`,
                path: PATH_MAIN.administrative.payouts,
                icon: ICONS.payouts,
                roles: [UserRole.SUPER_ADMIN],
            },
            {
                title: `${translate('layouts.navConfig.organizations')}`,
                path: PATH_MAIN.administrative.organizations,
                icon: ICONS.organizations,
                roles: [UserRole.SUPER_ADMIN],
            },
            {
                title: 'fax management',
                path: PATH_MAIN.administrative.faxManagement,
                icon: ICONS.fax,
                roles: [UserRole.SUPER_ADMIN],
            }
        );
    }
    // TODO claims2: Permissions
    if (
        user.role.name === UserRole.SUPER_ADMIN ||
        user.role.name === UserRole.BILLING_ADMIN ||
        user.role.name === UserRole.CC_MANAGER
    ) {
        adminItems.push({
            title: `${translate('layouts.navConfig.settings')}`,
            path: PATH_MAIN.administrative.settings,
            icon: ICONS.settings,
            roles: [UserRole.SUPER_ADMIN, UserRole.BILLING_ADMIN, UserRole.CC_MANAGER],
        });
    }
    if (adminItems.length > 0) {
        config.push({
            subheader: `${translate('layouts.navConfig.administrative')}`,
            items: adminItems,
        } as any);
    }

    const accountItems = [];

    // Community - For admins and providers with beta access enabled
    const communityAccessGrantRequired = user.role.name !== UserRole.PROVIDER;
    const communityAccessGranted = user.betaAccessSettings?.communityEnabled;
    const isElligibleRoleForCommunity = user.role.name !== UserRole.CLIENT;

    if (
        (!communityAccessGrantRequired ||
            (communityAccessGrantRequired && communityAccessGranted)) &&
        isElligibleRoleForCommunity
    ) {
        accountItems.push({
            title: `${translate('layouts.navConfig.community')}`,
            path: PATH_MAIN.community,
            icon: ICONS.community,
            roles: [UserRole.PROVIDER, UserRole.CC_MANAGER, UserRole.RECRUITER, ...ADMIN_ROLES],
        });
    }

    if (!isClient) {
        accountItems.push({
            title: `${translate('layouts.navConfig.myAccount')}`,
            path: PATH_MAIN.account,
            icon: ICONS.myAccount,
            roles: [UserRole.PROVIDER, ...ADMIN_ROLES],
        });
    }

    // Account

    if (accountItems.length > 0) {
        config.push({
            subheader: translate('layouts.navConfig.account'),
            items: accountItems,
        } as any);
    }

    return config;
};

export default navConfig;
